import { delay, http, HttpResponse } from "msw";

import { type ApiGatewayWorkbenchApiKey } from "@vega-api/generated/types/apiGatewayWorkbenchApiKey";
import { type ApiGatewayWorkbenchApiKeysResponse } from "@vega-api/generated/types/apiGatewayWorkbenchApiKeysResponse";
import { type InvestorDetails } from "@vega-api/generated/types/investorDetails";

const DAY_MS = 24 * 60 * 60 * 1000;
const daysFromNow = (days: number) =>
  new Date(Date.now() + days * DAY_MS).toISOString();

/**
 * Demo dataset for the workbench "Reporting API Key Mgmt." page: covers the
 * three derived statuses (incl. the expired-but-active=true backend quirk),
 * the soon-to-expire highlight, null-enrichment fallbacks, and never-used
 * keys, with dates kept relative to now so the demo doesn't go stale.
 */
const demoKeys: ApiGatewayWorkbenchApiKey[] = [
  {
    keyName: "acme-pension-reporting-prod",
    userId: "11111111-1111-1111-1111-111111111111",
    userName: "Ada Lovelace",
    organizationName: "Acme Pension Fund",
    active: true,
    createdAt: daysFromNow(-40),
    updatedAt: daysFromNow(-40),
    expiresAt: daysFromNow(325),
    lastUsedAt: daysFromNow(-1),
  },
  {
    keyName: "acme-pension-reporting-staging",
    userId: "11111111-1111-1111-1111-111111111111",
    userName: "Ada Lovelace",
    organizationName: "Acme Pension Fund",
    active: true,
    createdAt: daysFromNow(-200),
    updatedAt: daysFromNow(-200),
    expiresAt: daysFromNow(12),
    lastUsedAt: daysFromNow(-30),
  },
  {
    keyName: "globex-quarterly-sync",
    userId: "22222222-2222-2222-2222-222222222222",
    userName: "Grace Hopper",
    organizationName: "Globex LP",
    active: true,
    createdAt: daysFromNow(-400),
    updatedAt: daysFromNow(-400),
    expiresAt: daysFromNow(-35),
    lastUsedAt: daysFromNow(-36),
  },
  {
    keyName: "globex-legacy-feed",
    userId: "22222222-2222-2222-2222-222222222222",
    userName: "Grace Hopper",
    organizationName: "Globex LP",
    active: false,
    createdAt: daysFromNow(-500),
    updatedAt: daysFromNow(-90),
    expiresAt: daysFromNow(100),
    lastUsedAt: null,
  },
  {
    keyName: "orphaned-integration",
    userId: "33333333-3333-3333-3333-333333333333",
    userName: null,
    organizationName: null,
    active: true,
    createdAt: daysFromNow(-10),
    updatedAt: daysFromNow(-10),
    expiresAt: daysFromNow(355),
    lastUsedAt: null,
  },
];

type KeyStatusValue = "ACTIVE" | "DEACTIVATED" | "EXPIRED";

const deriveStatus = (key: ApiGatewayWorkbenchApiKey): KeyStatusValue => {
  if (!key.active) return "DEACTIVATED";

  return new Date(key.expiresAt).getTime() > Date.now() ? "ACTIVE" : "EXPIRED";
};

const matchesSearch = (key: ApiGatewayWorkbenchApiKey, needle: string) =>
  [key.keyName, key.userName ?? "", key.organizationName ?? ""].some((value) =>
    value.toLowerCase().includes(needle),
  );

const SORT_LAST = "￿";
const STATUS_ORDER: Record<KeyStatusValue, number> = {
  ACTIVE: 0,
  EXPIRED: 1,
  DEACTIVATED: 2,
};

const sortValue = (
  key: ApiGatewayWorkbenchApiKey,
  sortBy: string,
): number | string => {
  switch (sortBy) {
    case "KEY_NAME":
      return key.keyName.toLowerCase();
    case "USER_NAME":
      return key.userName?.toLowerCase() ?? SORT_LAST;
    case "ORGANIZATION_NAME":
      return key.organizationName?.toLowerCase() ?? SORT_LAST;
    case "STATUS":
      return STATUS_ORDER[deriveStatus(key)];
    case "UPDATED_AT":
      return key.updatedAt;
    case "EXPIRES_AT":
      return key.expiresAt;
    case "LAST_USED_AT":
      return key.lastUsedAt ?? SORT_LAST;
    default:
      return key.createdAt;
  }
};

/**
 * Serves a page of keys the way the backend does: filter by userId / derived
 * status / searchQuery, sort by sortBy/sortDirection (default newest-first),
 * then paginate. Exported so tests can serve their own datasets with the
 * same semantics.
 */
export const buildWorkbenchApiKeysResponse = (
  keys: ApiGatewayWorkbenchApiKey[],
  searchParams: URLSearchParams,
): ApiGatewayWorkbenchApiKeysResponse => {
  const page = Number(searchParams.get("page") ?? "1");
  const pageSize = Number(searchParams.get("pageSize") ?? "10");
  const searchQuery = searchParams.get("searchQuery")?.trim().toLowerCase();
  const status = searchParams.get("status");
  const userId = searchParams.get("userId");
  const sortBy = searchParams.get("sortBy");
  const sortDirection = searchParams.get("sortDirection");

  const matches = keys
    .filter((key) => userId === null || key.userId === userId)
    .filter((key) => status === null || deriveStatus(key) === status)
    .filter(
      (key) =>
        searchQuery === undefined ||
        searchQuery === "" ||
        matchesSearch(key, searchQuery),
    )
    .sort(
      sortBy === null
        ? // Backend default ordering: newest-created first.
          (a, b) => b.createdAt.localeCompare(a.createdAt)
        : (a, b) => {
            const left = sortValue(a, sortBy);
            const right = sortValue(b, sortBy);
            let compared = 0;

            if (left < right) compared = -1;

            if (left > right) compared = 1;

            return sortDirection === "DESC" ? -compared : compared;
          },
    );

  const totalPages = Math.ceil(matches.length / pageSize);

  return {
    data: matches.slice((page - 1) * pageSize, page * pageSize),
    meta: {
      count: matches.length,
      page,
      pageSize,
      totalPages,
      hasNextPage: page < totalPages,
    },
    errors: {},
  };
};

/**
 * Investor users matching the demo keys' owners, so the user admin page
 * (Investors -> user -> Reporting API Management) and the generate dialog's
 * picker line up with the key listing in MSW mode. The orphaned key's owner
 * (33333333-…) is deliberately absent - it demos a deleted user.
 */
const demoInvestors: InvestorDetails[] = [
  {
    id: "11111111-1111-1111-1111-111111111111",
    createdAt: daysFromNow(-400),
    updatedAt: daysFromNow(-40),
    firstName: "Ada",
    lastName: "Lovelace",
    email: "ada.lovelace@acme-pension.example",
    status: "ACTIVE",
    lastActivity: daysFromNow(-1),
    limitedPartnerId: "aaaaaaaa-1111-1111-1111-111111111111",
    limitedPartnerName: "Acme Pension Fund",
    contactPhone: "+1 555 0100",
    extPortalAccess: true,
  },
  {
    id: "22222222-2222-2222-2222-222222222222",
    createdAt: daysFromNow(-600),
    updatedAt: daysFromNow(-90),
    firstName: "Grace",
    lastName: "Hopper",
    email: "grace.hopper@globex.example",
    status: "ACTIVE",
    lastActivity: daysFromNow(-3),
    limitedPartnerId: "bbbbbbbb-2222-2222-2222-222222222222",
    limitedPartnerName: "Globex LP",
    contactPhone: "+1 555 0101",
    extPortalAccess: true,
  },
];

export const getWorkbenchInvestorsDemoHandler = () =>
  http.get("*/api/authentication/v1/workbench/investors", ({ request }) => {
    const searchQuery = new URL(request.url).searchParams
      .get("searchQuery")
      ?.trim()
      .toLowerCase();
    const matches = demoInvestors.filter(
      (investor) =>
        searchQuery === undefined ||
        searchQuery === "" ||
        [investor.firstName, investor.lastName, investor.email].some((value) =>
          value.toLowerCase().includes(searchQuery),
        ),
    );

    return HttpResponse.json({
      data: matches,
      meta: {
        count: matches.length,
        page: 1,
        pageSize: matches.length,
        totalPages: 1,
        hasNextPage: false,
      },
      errors: {},
    });
  });

export const getWorkbenchInvestorByIdDemoHandler = () =>
  http.get(
    "*/api/authentication/v1/workbench/investor/:investorId",
    ({ params }) => {
      const investor = demoInvestors.find(
        (candidate) => candidate.id === params.investorId,
      );

      // Unknown ids resolve to a deterministic placeholder rather than a 404:
      // the f2f suites visit investor pages with their own fixture ids, and
      // the previous auto-generated mock satisfied any id. (The generated
      // faker response cannot be used here - its shape crashes the page.)
      return HttpResponse.json({
        data: investor ?? {
          id: String(params.investorId),
          createdAt: daysFromNow(-100),
          updatedAt: daysFromNow(-10),
          firstName: "Demo",
          lastName: "Investor",
          email: "demo.investor@vega.example",
          status: "ACTIVE",
          lastActivity: daysFromNow(-1),
          limitedPartnerId: "cccccccc-3333-3333-3333-333333333333",
          limitedPartnerName: "Demo LP",
          contactPhone: "+1 555 0102",
          extPortalAccess: true,
        },
        errors: {},
      });
    },
  );

export const getReportingApiKeysHandler = () =>
  http.get("*/api/api-gateway/v1/workbench/keys", async ({ request }) => {
    await delay(10);

    return HttpResponse.json(
      buildWorkbenchApiKeysResponse(
        demoKeys,
        new URL(request.url).searchParams,
      ),
    );
  });
